import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ClearIcon from '@mui/icons-material/Clear';
import SettingsIcon from '@mui/icons-material/Settings';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined'; // elenco dati
import AppDocuServiceCall from './AppDocuServiceCall';
import { vehicleToString, notifySomething } from './AppDocuCommon';
import DialogCalibrationDetails from '../Vehicle_Components/DialogCalibrationDetails';

// Memoized vehicle checkbox item with internal state to prevent unnecessary re-renders
class VehicleCheckboxItem extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      checked: props.isChecked
    };
  }

  componentDidUpdate(prevProps) {
    // Update internal state only if parent explicitly changes the prop
    if (prevProps.isChecked !== this.props.isChecked) {
      this.setState({ checked: this.props.isChecked });
    }
  }

  // Method to be called by parent for bulk operations
  setChecked = (checked) => {
    this.setState({ checked });
  };

  handleChange = () => {
    const newChecked = !this.state.checked;
    this.setState({ checked: newChecked });
    this.props.onChange(newChecked);
  };

  render() {
    const { vehicle, vehicleRef } = this.props;
    const vehicleId = vehicle.id;
    
    return (
      <Box
        ref={vehicleRef}
        sx={{ transition: 'background-color 0.3s' }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={this.state.checked}
              onChange={this.handleChange}
              size="small"
            />
          }
          label={vehicleToString(vehicleId, vehicle)}
          sx={{ 
            display: 'flex', 
            width: '100%',
            m: 0,
            py: 0.25,
            '& .MuiFormControlLabel-label': {
              fontSize: '0.875rem',
              lineHeight: 1.2
            }
          }}
        />
      </Box>
    );
  }
}

// Memoized selected vehicle item
class SelectedVehicleItem extends React.PureComponent {
  render() {
    const { vehicle, onScroll, calibrationIds, onCalibrationClick } = this.props;
    const vehicleId = vehicle.id;
    
    return (
      <Box
        sx={{
          py: 0.5,
          borderBottom: '1px solid #f0f0f0',
          '&:last-child': { borderBottom: 'none' },
          display: 'flex',
          alignItems: 'center',
          gap: 0.5
        }}
      >
        <Button
          onClick={onScroll}
          sx={{
            textAlign: 'left',
            flex: 1,
            justifyContent: 'flex-start',
            textTransform: 'none',
            p: 0.5,
            minHeight: 'auto',
            fontSize: '0.875rem',
            lineHeight: 1.2,
            '&:hover': {
              backgroundColor: '#f5f5f5'
            }
          }}
        >
          {vehicleToString(vehicleId, vehicle)}
        </Button>
        {calibrationIds && calibrationIds.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.25 }}>
            {calibrationIds.map((calibrationId, index) => {
              const isNull = calibrationId === null || calibrationId === undefined;
              return (
                <Tooltip 
                  key={index} 
                  title={isNull ? 'No calibration associated (packet only)' : `Calibration ID: ${calibrationId}. Click to view details.`}
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        if (!isNull) {
                          e.stopPropagation();
                          onCalibrationClick(calibrationId);
                        }
                      }}
                      disabled={isNull}
                      sx={{ p: 0.25 }}
                    >
                      <ListAltOutlinedIcon 
                        fontSize="small" 
                        sx={{ color: isNull ? 'grey' : 'green' }} 
                      />
                    </IconButton>
                  </span>
                </Tooltip>
              );
            })}
          </Box>
        )}
      </Box>
    );
  }
}

// Separate component for selected vehicles list to control its re-renders independently
class SelectedVehiclesList extends React.Component {
  shouldComponentUpdate(nextProps) {
    // Update when counter changes (indicating selection changed)
    return (
      nextProps.selectionUpdateCounter !== this.props.selectionUpdateCounter ||
      nextProps.allVehicles !== this.props.allVehicles
    );
  }

  render() {
    const { allVehicles, selectedVehicleIds, onScroll, vehicleCalibrations, onCalibrationClick } = this.props;
    
    return (
      <>
        {selectedVehicleIds.size === 0 && (
          <Typography color="text.secondary" variant="body2">
            No vehicles selected
          </Typography>
        )}
        {allVehicles
          .filter(v => selectedVehicleIds.has(v.id))
          .map((vehicle) => (
            <SelectedVehicleItem
              key={vehicle.id}
              vehicle={vehicle}
              onScroll={() => onScroll(vehicle.id)}
              calibrationIds={vehicleCalibrations[vehicle.id]}
              onCalibrationClick={onCalibrationClick}
            />
          ))}
      </>
    );
  }
}

export default class PacketVehicleAssignDialog extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      saving: false,
      allVehicles: [], // All available vehicles
      selectedVehicleIds: new Set(), // Currently selected vehicle IDs
      initialVehicleIds: new Set(), // Original selected vehicle IDs (for tracking changes)
      filterText: '', // Filter text input
      selectionUpdateCounter: 0, // Counter to trigger selected list updates
      showConfirmDialog: false, // Show confirmation dialog before save
      packetName: '', // Name of the packet
      selectedCalibrationId: null, // For DialogCalibrationDetails
      vehicleCalibrations: {}, // Map of vehicle_id -> array of calibration_ids associated with this packet
    };
    this.vehicleRefs = {}; // Refs for scrolling to vehicles
    this.checkboxRefs = {}; // Refs to checkbox components for bulk operations
  }

  async componentDidMount() {
    // Load data when component mounts
    if (this.props.packetId) {
      this.loadData();
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    // Only update for loading, saving, filter changes, vehicle list changes, or selection updates
    return (
      this.props.packetId !== nextProps.packetId ||
      this.state.loading !== nextState.loading ||
      this.state.saving !== nextState.saving ||
      this.state.filterText !== nextState.filterText ||
      this.state.allVehicles.length !== nextState.allVehicles.length ||
      this.state.selectionUpdateCounter !== nextState.selectionUpdateCounter ||
      this.state.showConfirmDialog !== nextState.showConfirmDialog ||
      this.state.selectedCalibrationId !== nextState.selectedCalibrationId
    );
  }

  async componentDidUpdate(prevProps) {
    // When packet ID changes, load data
    if (this.props.packetId && this.props.packetId !== prevProps.packetId) {
      this.loadData();
    }
  }

  async loadData() {
    this.setState({ loading: true });
    try {
      // Fetch packet details to get the name
      const packetRes = await AppDocuServiceCall.getPacket(this.props.packetId);
      const packetName = packetRes.data?.package?.name || '';

      // Fetch all vehicles
      const allVehiclesRes = await AppDocuServiceCall.listVehicles();
      if (allVehiclesRes.data && allVehiclesRes.data.is_valid === false) {
        notifySomething(allVehiclesRes.data.message || 'Error fetching vehicles', 'error');
        this.setState({ loading: false });
        return;
      }

      // Fetch vehicles associated with this packet
      const assocVehiclesRes = await AppDocuServiceCall.getPacketVehicles(this.props.packetId);
      if (assocVehiclesRes.data && assocVehiclesRes.data.is_valid === false) {
        notifySomething(assocVehiclesRes.data.message || 'Error fetching associated vehicles', 'error');
        this.setState({ loading: false });
        return;
      }

      const allVehicles = (allVehiclesRes.data.machines || []).sort((a, b) => {
        // Sort by brand, then model, then engine_code
        const brandCompare = (a.brand || '').localeCompare(b.brand || '');
        if (brandCompare !== 0) return brandCompare;
        const modelCompare = (a.model || '').localeCompare(b.model || '');
        if (modelCompare !== 0) return modelCompare;
        return (a.engine_code || '').localeCompare(b.engine_code || '');
      });
      const associatedVehicles = assocVehiclesRes.data.machines || [];
      const associations = assocVehiclesRes.data.associations || [];

      // Create a Set of associated vehicle IDs (use machine_id from associated vehicles)
      const selectedVehicleIds = new Set(associatedVehicles.map(v => v.machine_id || v.id));
      const initialVehicleIds = new Set(selectedVehicleIds);

      // Build map of vehicle_id -> calibration_ids for this packet
      // Process associations array to extract calibration_ids per machine
      // Include null to show gray non-clickable icons
      const vehicleCalibrations = {};
      associations.forEach(assoc => {
        const vehicleId = assoc.machine_id;
        const calibrationId = assoc.calibration_id;
        
        // Include both null and non-null calibration_ids
        if (!vehicleCalibrations[vehicleId]) {
          vehicleCalibrations[vehicleId] = [];
        }
        // Store null as-is, or actual calibration_id
        // Avoid duplicates (only for non-null values)
        if (calibrationId === null || calibrationId === undefined) {
          vehicleCalibrations[vehicleId].push(null);
        } else {
          if (!vehicleCalibrations[vehicleId].includes(calibrationId)) {
            vehicleCalibrations[vehicleId].push(calibrationId);
          }
        }
      });

      // Sort calibration_ids: null first, then by calibration_id ascending
      Object.keys(vehicleCalibrations).forEach(vehicleId => {
        vehicleCalibrations[vehicleId].sort((a, b) => {
          if (a === null || a === undefined) return -1;
          if (b === null || b === undefined) return 1;
          return a - b;
        });
      });

      console.log('All vehicles loaded:', allVehicles.length);
      console.log('Associated vehicles:', associatedVehicles.length);
      console.log('Selected IDs:', Array.from(selectedVehicleIds));
      console.log('Associations:', associations);
      console.log('Vehicle calibrations:', vehicleCalibrations);

      this.setState({
        allVehicles,
        selectedVehicleIds,
        initialVehicleIds,
        packetName,
        vehicleCalibrations,
        loading: false,
      });
    } catch (error) {
      notifySomething('Error loading data', 'error');
      console.error(error);
      this.setState({ loading: false });
    }
  }

  handleCheckboxChange = (vehicleId, isChecked) => {
    // Update the Set based on the checkbox's new state
    if (isChecked) {
      this.state.selectedVehicleIds.add(vehicleId);
    } else {
      this.state.selectedVehicleIds.delete(vehicleId);
    }
    // Increment counter to trigger selected list update without full re-render
    this.setState({ selectionUpdateCounter: this.state.selectionUpdateCounter + 1 });
  };

  handleFilterChange = (event) => {
    this.setState({ filterText: event.target.value });
  };

  handleSelectAllVisible = () => {
    const filteredVehicles = this.getFilteredVehicles();
    filteredVehicles.forEach(v => {
      this.state.selectedVehicleIds.add(v.id);
      // Update checkbox component state directly
      const checkboxRef = this.checkboxRefs[v.id];
      if (checkboxRef && checkboxRef.current) {
        checkboxRef.current.setChecked(true);
      }
    });
    // Trigger selected list update
    this.setState({ selectionUpdateCounter: this.state.selectionUpdateCounter + 1 });
  };

  handleDeselectAllVisible = () => {
    const filteredVehicles = this.getFilteredVehicles();
    filteredVehicles.forEach(v => {
      this.state.selectedVehicleIds.delete(v.id);
      // Update checkbox component state directly
      const checkboxRef = this.checkboxRefs[v.id];
      if (checkboxRef && checkboxRef.current) {
        checkboxRef.current.setChecked(false);
      }
    });
    // Trigger selected list update
    this.setState({ selectionUpdateCounter: this.state.selectionUpdateCounter + 1 });
  };

  getFilteredVehicles() {
    const { allVehicles, filterText } = this.state;
    
    if (!filterText.trim()) {
      return allVehicles;
    }

    // Split by semicolon for AND search
    const filters = filterText.split(';').map(f => f.trim().toLowerCase()).filter(f => f);
    
    return allVehicles.filter(vehicle => {
      const brand = (vehicle.brand || '').toLowerCase();
      const model = (vehicle.model || '').toLowerCase();
      const engineCode = (vehicle.engine_code || '').toLowerCase();
      const vehicleId = String(vehicle.id || '').toLowerCase();
      const searchText = `${brand} ${model} ${engineCode} ${vehicleId}`;
      
      // All filters must match (AND logic)
      return filters.every(filter => searchText.includes(filter));
    });
  }

  scrollToVehicle = (vehicleId) => {
    const ref = this.vehicleRefs[vehicleId];
    if (ref && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Highlight briefly
      ref.current.style.backgroundColor = '#fff3cd';
      setTimeout(() => {
        ref.current.style.backgroundColor = '';
      }, 1000);
    }
  };

  handleSave = () => {
    this.setState({ showConfirmDialog: true });
  };

  handleCancelConfirm = () => {
    this.setState({ showConfirmDialog: false });
  };

  async confirmSave() {
    this.setState({ showConfirmDialog: false, saving: true });
    try {
      const { selectedVehicleIds, initialVehicleIds } = this.state;
      const { packetId } = this.props;

      // Find vehicles to add (in selected but not in initial)
      const vehicleIdsToAdd = Array.from(selectedVehicleIds).filter(id => !initialVehicleIds.has(id));
      
      // Find vehicles to remove (in initial but not in selected)
      const vehicleIdsToRemove = Array.from(initialVehicleIds).filter(id => !selectedVehicleIds.has(id));

      // Execute bulk operations
      const promises = [];
      
      // Add packet to multiple vehicles in one call
      if (vehicleIdsToAdd.length > 0) {
        promises.push(
          AppDocuServiceCall.addPacketToMachines(packetId, vehicleIdsToAdd)
        );
      }
      
      // Remove packet from multiple vehicles in one call
      if (vehicleIdsToRemove.length > 0) {
        promises.push(
          AppDocuServiceCall.removePacketFromMachines(packetId, vehicleIdsToRemove)
        );
      }

      // Execute operations in parallel (if both add and remove are needed)
      await Promise.all(promises);

      notifySomething(`Successfully updated associations: ${vehicleIdsToAdd.length} added, ${vehicleIdsToRemove.length} removed`, 'success');
      
      // Update initialVehicleIds to reflect current state
      this.setState({ 
        initialVehicleIds: new Set(selectedVehicleIds),
        saving: false 
      });

      // Optionally close dialog or notify parent
      if (this.props.onSave) {
        this.props.onSave();
      }
    } catch (error) {
      notifySomething('Error saving vehicle associations', 'error');
      console.error(error);
      this.setState({ saving: false });
    }
  }

  render() {
    const { onClose, packetId } = this.props;
    const { loading, saving, selectedVehicleIds, filterText } = this.state;
    const open = !!packetId;

    const filteredVehicles = this.getFilteredVehicles();
    const allFilteredSelected = filteredVehicles.length > 0 && filteredVehicles.every(v => selectedVehicleIds.has(v.id));
    const someFilteredSelected = filteredVehicles.some(v => selectedVehicleIds.has(v.id));

    return (
      <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
        {this.state.packetName && (
          <DialogTitle>          
            {"Assign"}
            <Typography component="span" sx={{ color: 'warning.main', fontWeight: 'bold', ml: 1, mr: 1 }}>
              {this.state.packetName}
            </Typography>
            {"to Vehicles"}
          </DialogTitle>
        )}
        <DialogContent sx={{ pt: 2 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Filter input */}
              <TextField
                fullWidth
                label="Filter (use ; for AND, e.g. 'BMW;320;N47')"
                placeholder="Search by Brand, Model, Engine Code, or ID"
                value={filterText}
                onChange={this.handleFilterChange}
                variant="outlined"
                size="small"
                sx={{ mt: 1 }}
                InputProps={{
                  endAdornment: filterText && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => this.setState({ filterText: '' })}
                        edge="end"
                        size="small"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Divider sx={{ mb: 1 }} />

              {/* Two column layout */}
              <Box sx={{ display: 'flex', gap: 2, height: '600px' }}>
                {/* Left column: All vehicles with checkboxes */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Stack direction="row" alignItems="center" spacing={0.25} sx={{ mb: 1, minHeight: '32px' }}>
                    <Tooltip title="Select All Visible">
                      <span>
                        <IconButton 
                          size="small" 
                          onClick={this.handleSelectAllVisible}
                          disabled={allFilteredSelected}
                          sx={{ p: 0.25 }}
                        >
                          <CheckCircleOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Deselect All Visible">
                      <span>
                        <IconButton 
                          size="small" 
                          onClick={this.handleDeselectAllVisible}
                          disabled={!someFilteredSelected}
                          sx={{ p: 0.25 }}
                        >
                          <RadioButtonUncheckedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', ml: 0.5, pt: 0.5, pl: 1 }}>
                      {filterText.trim() ? `Filtered Vehicles (${filteredVehicles.length})` : `All Vehicles (${filteredVehicles.length})`}
                    </Typography>
                  </Stack>
                  <Box sx={{ flex: 1, overflowY: 'auto', border: '2px solid #e0e0e0', borderRadius: 2, p: 1 }}>
                    {filteredVehicles.length === 0 && (
                      <Typography color="text.secondary">
                        {filterText.trim() ? 'No vehicles match the filter' : 'No vehicles available'}
                      </Typography>
                    )}
                    {filteredVehicles.map((vehicle) => {
                      const vehicleId = vehicle.id;
                      if (!this.vehicleRefs[vehicleId]) {
                        this.vehicleRefs[vehicleId] = React.createRef();
                      }
                      if (!this.checkboxRefs[vehicleId]) {
                        this.checkboxRefs[vehicleId] = React.createRef();
                      }
                      return (
                        <VehicleCheckboxItem
                          key={vehicleId}
                          ref={this.checkboxRefs[vehicleId]}
                          vehicle={vehicle}
                          isChecked={selectedVehicleIds.has(vehicleId)}
                          onChange={(isChecked) => this.handleCheckboxChange(vehicleId, isChecked)}
                          vehicleRef={this.vehicleRefs[vehicleId]}
                        />
                      );
                    })}
                  </Box>
                </Box>
                {/* Right column: Selected vehicles */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ mb: 1, minHeight: '32px', display: 'flex', alignItems: 'center' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', ml: 0, pt: 0.5 }}>
                      Selected ({selectedVehicleIds.size})
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, overflowY: 'auto', border: '2px solid #e0e0e0', borderRadius: 2, p: 1 }}>
                    <SelectedVehiclesList
                      allVehicles={this.state.allVehicles}
                      selectedVehicleIds={selectedVehicleIds}
                      selectionUpdateCounter={this.state.selectionUpdateCounter}
                      onScroll={this.scrollToVehicle}
                      vehicleCalibrations={this.state.vehicleCalibrations}
                      onCalibrationClick={(calibrationId) => this.setState({ selectedCalibrationId: calibrationId })}
                    />
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={this.handleSave}
            variant="contained"
            disabled={loading || saving}
            startIcon={saving && <CircularProgress size={16} />}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>

        {/* Confirmation Dialog */}
        <Dialog open={this.state.showConfirmDialog} onClose={this.handleCancelConfirm}>
          <DialogTitle>Confirm Save</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to save these changes?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleCancelConfirm}>Cancel</Button>
            <Button onClick={() => this.confirmSave()} variant="contained" color="primary">
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        {/* Calibration Details Dialog */}
        <DialogCalibrationDetails
          calibrationId={this.state.selectedCalibrationId}
          open={!!this.state.selectedCalibrationId}
          onClose={() => this.setState({ selectedCalibrationId: null })}
        />
      </Dialog>
    );
  }
}
